import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Briefcase, Copy, LayoutGrid, List, MapPin, Plus, Search, Users } from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CardSkeleton, EmptyState, PageHeader, StatusBadge } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SelectField } from "@/components/forms/Fields";
import JobForm from "@/components/forms/JobForm";

function FunnelStrip({ funnel }) {
  const stages = ["Applications", "Screened", "Shortlisted", "Submitted", "Interview", "Offer", "Joined"];
  const max = Math.max(1, funnel?.Applications || 1);
  return (
    <div className="flex items-end gap-1">
      {stages.map((s) => (
        <div key={s} className="flex-1" title={`${s}: ${funnel?.[s] ?? 0}`}>
          <div className="h-8 overflow-hidden rounded-sm bg-slate-200">
            <div
              className="w-full bg-blue-500"
              style={{ height: `${Math.min(100, ((funnel?.[s] ?? 0) / max) * 100)}%`, marginTop: `${100 - Math.min(100, ((funnel?.[s] ?? 0) / max) * 100)}%` }}
            />
          </div>
          <p className="font-mono-num mt-1 text-center text-[10px] text-slate-500">{funnel?.[s] ?? 0}</p>
        </div>
      ))}
    </div>
  );
}

const JobCard = ({ job, onOpen, onDuplicate, canEdit }) => (
  <article
    data-testid="job-card"
    className="surface surface-hover cursor-pointer rounded-md p-5"
    onClick={() => onOpen(job)}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-mono-num text-[10px] text-slate-400">{job.job_code}</p>
        <h3 className="mt-1 truncate text-[15px] font-medium text-slate-900">{job.title}</h3>
        <p className="mt-0.5 truncate text-xs text-slate-500">{job.client_name || "No client linked"}</p>
      </div>
      <StatusBadge value={job.status} testid="job-status-badge" />
    </div>

    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[11px] text-slate-500">
      <span className="inline-flex items-center gap-1.5">
        <MapPin className="h-3 w-3" /> {job.location || "Not available"}
      </span>
      <span className="font-mono-num">
        {job.min_experience ?? "?"}–{job.max_experience ?? "?"} yrs
      </span>
      <span className="font-mono-num">
        {job.min_ctc ?? "?"}–{job.max_ctc ?? "?"} LPA
      </span>
      <span>{job.work_mode}</span>
    </div>

    <div className="mt-4 flex flex-wrap gap-1.5">
      {(job.required_skills || []).slice(0, 4).map((s) => (
        <span key={s} className="rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">
          {s}
        </span>
      ))}
      {(job.required_skills || []).length > 4 && (
        <span className="text-[10px] text-slate-400">+{job.required_skills.length - 4}</span>
      )}
    </div>

    <div className="mt-5 border-t border-slate-200 pt-4">
      <FunnelStrip funnel={job.funnel} />
    </div>

    <div className="mt-4 flex items-center justify-between text-[11px] text-slate-400">
      <span className="inline-flex items-center gap-1.5">
        <Users className="h-3 w-3" /> {job.applicant_count} in pipeline
      </span>
      <span className="flex items-center gap-3">
        <StatusBadge value={job.priority} />
        {canEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(job);
            }}
            data-testid="job-duplicate-button"
            aria-label="Duplicate job"
            className="text-slate-500 hover:text-blue-600"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>
  </article>
);

export default function Jobs() {
  const { meta, can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [view, setView] = useState("grid");
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState(params.get("search") || "");
  const [filters, setFilters] = useState({
    status: params.get("status") || "all",
    client_id: "all",
    recruiter_id: "all",
    priority: "all",
  });

  const load = useCallback(async () => {
    setData(null);
    try {
      const res = await api.get("/jobs", {
        params: { search: search || undefined, ...filters, limit: 60 },
      });
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
      setData({ items: [], total: 0 });
    }
  }, [search, filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setFilter = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  const duplicate = async (job) => {
    try {
      const { data: clone } = await api.post(`/jobs/${job.id}/duplicate`);
      toast.success(`Duplicated as ${clone.job_code} (draft)`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openJob = (job) => navigate(`/jobs/${job.id}`);

  return (
    <div className="space-y-6">
      <PageHeader
        meta="Requirements"
        title="Jobs"
        subtitle="Live requirements across automotive and semiconductor accounts, with pipeline health at a glance."
        actions={
          <>
            <div className="flex overflow-hidden rounded-full border border-slate-200">
              <button
                onClick={() => setView("grid")}
                data-testid="jobs-grid-view"
                aria-label="Grid view"
                className={`px-3 py-2 ${view === "grid" ? "bg-blue-100 text-blue-700" : "text-slate-500"}`}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setView("list")}
                data-testid="jobs-list-view"
                aria-label="List view"
                className={`px-3 py-2 ${view === "list" ? "bg-blue-100 text-blue-700" : "text-slate-500"}`}
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
            {can("jobs", "create") && (
              <Button onClick={() => setShowForm(true)} data-testid="create-job-button" className="h-9 gap-2 rounded-full bg-blue-600 px-4 text-xs hover:bg-blue-500">
                <Plus className="h-3.5 w-3.5" /> Create job
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-3 md:grid-cols-[1.4fr_repeat(4,1fr)]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="jobs-search-input"
            placeholder="Search by title, code, skill or location"
            className="h-9 border-slate-200 bg-slate-50 pl-9 text-sm text-slate-800 placeholder:text-slate-400"
          />
        </div>
        <SelectField value={filters.status} onChange={setFilter("status")} allowAll options={meta?.enums?.job_statuses || []} testid="jobs-status-filter" placeholder="Status" />
        <SelectField value={filters.client_id} onChange={setFilter("client_id")} allowAll testid="jobs-client-filter" placeholder="Client"
          options={(meta?.clients || []).map((c) => ({ value: c.id, label: c.name }))} />
        <SelectField value={filters.recruiter_id} onChange={setFilter("recruiter_id")} allowAll testid="jobs-recruiter-filter" placeholder="Recruiter"
          options={(meta?.users || []).map((u) => ({ value: u.id, label: u.name }))} />
        <SelectField value={filters.priority} onChange={setFilter("priority")} allowAll options={meta?.enums?.priorities || []} testid="jobs-priority-filter" placeholder="Priority" />
      </div>

      {!data ? (
        <CardSkeleton count={6} />
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          testid="jobs-empty-state"
          title="No jobs match these filters"
          description="Try clearing the filters or create a new requirement to get started."
          action={
            can("jobs", "create") && (
              <Button onClick={() => setShowForm(true)} className="rounded-full bg-blue-600 hover:bg-blue-500">
                Create a job
              </Button>
            )
          }
        />
      ) : view === "grid" ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {data.items.map((j) => (
            <JobCard key={j.id} job={j} onOpen={openJob} onDuplicate={duplicate} canEdit={can("jobs", "edit")} />
          ))}
        </div>
      ) : (
        <div className="surface overflow-x-auto rounded-md thin-scroll">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 hover:bg-transparent">
                {["Job", "Client", "Location", "Experience", "CTC", "Pipeline", "Priority", "Status", "Opened"].map((h) => (
                  <TableHead key={h} className="label-xs whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((j) => (
                <TableRow
                  key={j.id}
                  onClick={() => openJob(j)}
                  data-testid="job-row"
                  className="cursor-pointer border-slate-200 odd:bg-slate-50/80 hover:bg-slate-100"
                >
                  <TableCell className="min-w-[200px]">
                    <p className="text-sm text-slate-900">{j.title}</p>
                    <p className="font-mono-num text-[10px] text-slate-400">{j.job_code}</p>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{j.client_name || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{j.location || "—"}</TableCell>
                  <TableCell className="font-mono-num whitespace-nowrap text-xs text-slate-500">
                    {j.min_experience ?? "?"}–{j.max_experience ?? "?"} yrs
                  </TableCell>
                  <TableCell className="font-mono-num whitespace-nowrap text-xs text-slate-500">
                    {j.min_ctc ?? "?"}–{j.max_ctc ?? "?"}
                  </TableCell>
                  <TableCell className="font-mono-num text-xs text-slate-600">{j.applicant_count}</TableCell>
                  <TableCell><StatusBadge value={j.priority} /></TableCell>
                  <TableCell><StatusBadge value={j.status} /></TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmtDate(j.opening_date)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {data && data.items.length > 0 && (
        <p className="text-xs text-slate-400">
          Showing {data.items.length} of {data.total} requirements
        </p>
      )}

      <JobForm open={showForm} onOpenChange={setShowForm} onSaved={(j) => navigate(`/jobs/${j.id}`)} />
    </div>
  );
}
